"use client";
import { motion } from "framer-motion";
import {
    AlertCircle,
    BookMarked,
    GitCommit,
    GitPullRequest,
    Star,
} from "lucide-react";
import { useEffect, useState } from "react";

interface GitHubStatsData {
    totalCommits: number;
    totalPRs: number;
    totalIssues: number;
    totalStars: number;
    totalRepos: number;
    updatedAt?: string;
}

export default function GitHubStats() {
    const [stats, setStats] = useState<GitHubStatsData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await fetch("/api/github-stats");
                const data = await res.json();
                if (data.success && data.data) {
                    setStats(data.data);
                }
            } catch (err) {
                console.error("Failed to load github stats", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
    }, []);

    const statItems = [
        {
            label: "Total Commits",
            value: stats ? `${stats.totalCommits}+` : "350+",
            icon: GitCommit,
            color: "text-emerald-400",
        },
        {
            label: "Pull Requests",
            value: stats ? `${stats.totalPRs}+` : "25+",
            icon: GitPullRequest,
            color: "text-purple-400",
        },
        {
            label: "Issues Handled",
            value: stats ? `${stats.totalIssues}+` : "15+",
            icon: AlertCircle,
            color: "text-amber-400",
        },
        {
            label: "Stars Earned",
            value: stats ? `${stats.totalStars}` : "10",
            icon: Star,
            color: "text-yellow-400",
        },
        {
            label: "Repositories",
            value: stats ? `${stats.totalRepos}` : "20",
            icon: BookMarked,
            color: "text-sky-400",
        },
    ];

    return (
        <section className="my-8 w-full">
            <div className="mb-4 flex items-center justify-between px-1">
                <h3 className="flex items-center gap-2 text-xl font-bold text-primary">
                    <GitCommit className="h-5 w-5 text-primary" />
                    GitHub Open Source Activity
                </h3>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                {statItems.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                        <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.08, duration: 0.3 }}
                            className="flex flex-col items-center justify-center rounded-2xl border border-border-color bg-card-bg p-4 text-center transition duration-300 hover:border-primary hover:shadow-sm"
                        >
                            <Icon className={`mb-2 h-6 w-6 ${item.color}`} />
                            <p className="text-2xl font-black tracking-tight text-text-color">
                                {isLoading ? (
                                    <span className="inline-block h-6 w-12 animate-pulse rounded bg-border-color" />
                                ) : (
                                    item.value
                                )}
                            </p>
                            <p className="text-muted-foreground mt-1 text-xs font-medium">
                                {item.label}
                            </p>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}
